import React, { useState, useEffect } from 'react';
import { Building2, Users, Edit, Trash2, UserPlus } from 'lucide-react';
import { companyService, Company } from '../../services/companyService';
import { useCompany } from '../../contexts/CompanyContext';

const CompanyManagement: React.FC = () => {
  const { currentCompany, refreshCompanies } = useCompany();
  const [companyDetails, setCompanyDetails] = useState<Company | null>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('member');

  useEffect(() => {
    if (currentCompany) {
      loadCompanyDetails();
      loadMembers();
    }
  }, [currentCompany]);

  const loadCompanyDetails = async () => {
    if (!currentCompany) return;

    try {
      const { data, error } = await companyService.getCompanyById(currentCompany.id);
      if (error) {
        console.error('Error loading company details:', error);
        return;
      }
      setCompanyDetails(data);
    } catch (error) {
      console.error('Error loading company details:', error);
    }
  };

  const loadMembers = async () => {
    if (!currentCompany) return;

    try {
      setLoading(true);
      const { data, error } = await companyService.getCompanyMembers(currentCompany.id);
      if (error) {
        console.error('Error loading members:', error);
        return;
      }
      setMembers(data || []);
    } catch (error) {
      console.error('Error loading members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!currentCompany || !newMemberEmail) return;

    try {
      // In a real implementation, you would search for the user by email first
      // For now, this is a placeholder
      alert('Funcionalidade de adicionar membro será implementada');
      setShowAddMemberModal(false);
      setNewMemberEmail('');
      setNewMemberRole('member');
    } catch (error) {
      console.error('Error adding member:', error);
    }
  };

  const handleUpdateRole = async (membershipId: string, newRole: string) => {
    try {
      const { error } = await companyService.updateUserRole(membershipId, newRole);
      if (error) {
        console.error('Error updating role:', error);
        alert('Erro ao atualizar função do membro');
        return;
      }
      await loadMembers();
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  const handleRemoveMember = async (membershipId: string) => {
    if (!confirm('Tem certeza que deseja remover este membro?')) return;

    try {
      const { error } = await companyService.removeUserFromCompany(membershipId);
      if (error) {
        console.error('Error removing member:', error);
        alert('Erro ao remover membro');
        return;
      }
      await loadMembers();
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };

  if (!currentCompany) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">
          Nenhuma empresa selecionada
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Company Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {companyDetails?.name || currentCompany.name}
              </h1>
              <p className="text-sm text-gray-500">
                CNPJ: {companyDetails?.cnpj || currentCompany.cnpj}
              </p>
            </div>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Edit className="w-4 h-4" />
            Editar Empresa
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Razão Social</label>
            <p className="text-gray-900">{companyDetails?.legal_name || '-'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Email</label>
            <p className="text-gray-900">{companyDetails?.email || '-'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Telefone</label>
            <p className="text-gray-900">{companyDetails?.phone || '-'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Plano</label>
            <p className="text-gray-900 capitalize">{companyDetails?.subscription_plan || '-'}</p>
          </div>
        </div>
      </div>

      {/* Members Management */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Membros da Empresa</h2>
          </div>
          <button
            onClick={() => setShowAddMemberModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <UserPlus className="w-4 h-4" />
            Adicionar Membro
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Carregando membros...</div>
        ) : members.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Nenhum membro encontrado</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Nome</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Função</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Ações</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900">
                      {member.user?.full_name || '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {member.user?.email || '-'}
                    </td>
                    <td className="py-3 px-4">
                      <select
                        value={member.role}
                        onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="member">Membro</option>
                        <option value="manager">Gerente</option>
                        <option value="admin">Administrador</option>
                      </select>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        member.is_active 
                          ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                      }`}>
                        {member.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Adicionar Membro</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email do Usuário
                </label>
                <input
                  type="email"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="usuario@exemplo.com.br"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Função
                </label>
                <select
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="member">Membro</option>
                  <option value="manager">Gerente</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddMemberModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddMember}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyManagement;